import React, { useEffect, useState } from "react";
import { CalendarClock, Save } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { useToast } from "../hooks/use-toast";
import api from "../api";

function Card({ children, className = "" }) {
  return <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6 ${className}`}>{children}</div>;
}

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const STATUS = ["Available Now", "Available Soon", "Not Currently Available"];
const WORK = ["Full-Time", "Part-Time", "Flexible", "Other"];
const TZ = ["Eastern (ET)", "Central (CT)", "Mountain (MT)", "Pacific (PT)", "Alaska (AKT)", "Hawaii (HT)", "Other"];

const emptySchedule = () => DAYS.reduce((a, d) => ({ ...a, [d]: { available: d !== "saturday" && d !== "sunday", start: "09:00", end: "17:00" } }), {});

export default function Availability() {
  const { toast } = useToast();
  const [form, setForm] = useState({ status: "", preferred_work_type: "", earliest_start_date: "", preferred_hours_per_week: "", timezone: "", notes: "" });
  const [schedule, setSchedule] = useState(emptySchedule());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/availability").then((r) => {
      if (r.data) {
        setForm({
          status: r.data.status || "", preferred_work_type: r.data.preferred_work_type || "",
          earliest_start_date: r.data.earliest_start_date || "", preferred_hours_per_week: r.data.preferred_hours_per_week ?? "",
          timezone: r.data.timezone || "", notes: r.data.notes || "",
        });
        if (r.data.weekly_schedule && Object.keys(r.data.weekly_schedule).length) setSchedule({ ...emptySchedule(), ...r.data.weekly_schedule });
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const setDay = (day, key, val) => setSchedule((s) => ({ ...s, [day]: { ...s[day], [key]: val } }));

  const validate = () => {
    for (const d of DAYS) {
      const v = schedule[d];
      if (v.available && v.start && v.end && v.end <= v.start) {
        toast({ title: "Invalid times", description: `${d[0].toUpperCase() + d.slice(1)}: end time must be after start time.`, variant: "destructive" });
        return false;
      }
    }
    return true;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await api.put("/availability", {
        ...form,
        preferred_hours_per_week: form.preferred_hours_per_week === "" ? null : Number(form.preferred_hours_per_week),
        earliest_start_date: form.earliest_start_date || null,
        weekly_schedule: schedule,
      });
      toast({ title: "Availability saved successfully." });
    } catch (err) {
      toast({ title: "Could not save availability", description: err.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-sm text-slate-400">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="availability-page">
      <Card>
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-violet-100"><CalendarClock className="h-5 w-5 text-violet-600" /></div>
          <div>
            <h2 className="text-base font-bold text-slate-900">My Availability</h2>
            <p className="text-sm text-slate-500">Tell us when you can work. This is saved to your profile and shared with your onboarding team.</p>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-1.5">
            <Label>Availability status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="availability-status"><SelectValue placeholder="Select status" /></SelectTrigger>
              <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Preferred work type</Label>
            <Select value={form.preferred_work_type} onValueChange={(v) => setForm({ ...form, preferred_work_type: v })}>
              <SelectTrigger data-testid="availability-worktype"><SelectValue placeholder="Select type" /></SelectTrigger>
              <SelectContent>{WORK.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Time zone</Label>
            <Select value={form.timezone} onValueChange={(v) => setForm({ ...form, timezone: v })}>
              <SelectTrigger data-testid="availability-timezone"><SelectValue placeholder="Select time zone" /></SelectTrigger>
              <SelectContent>{TZ.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Earliest start date</Label>
            <Input type="date" value={form.earliest_start_date} onChange={(e) => setForm({ ...form, earliest_start_date: e.target.value })} data-testid="availability-start-date" />
          </div>
          <div className="space-y-1.5">
            <Label>Preferred hours / week</Label>
            <Input type="number" min="0" max="80" value={form.preferred_hours_per_week} onChange={(e) => setForm({ ...form, preferred_hours_per_week: e.target.value })} data-testid="availability-hours" placeholder="e.g. 40" />
          </div>
        </div>
      </Card>

      <Card>
        <h3 className="text-sm font-bold text-slate-900">Weekly schedule</h3>
        <p className="text-sm text-slate-500">Toggle each day and set your hours.</p>
        <div className="mt-4 space-y-2">
          {DAYS.map((d) => (
            <div key={d} className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-100 p-3" data-testid={`day-row-${d}`}>
              <div className="flex w-40 items-center gap-3">
                <Switch checked={schedule[d].available} onCheckedChange={(v) => setDay(d, "available", v)} data-testid={`day-toggle-${d}`} />
                <span className="text-sm font-medium capitalize text-slate-800">{d}</span>
              </div>
              {schedule[d].available ? (
                <div className="flex items-center gap-2 text-sm">
                  <Input type="time" value={schedule[d].start} onChange={(e) => setDay(d, "start", e.target.value)} className="w-32" data-testid={`day-start-${d}`} />
                  <span className="text-slate-400">to</span>
                  <Input type="time" value={schedule[d].end} onChange={(e) => setDay(d, "end", e.target.value)} className="w-32" data-testid={`day-end-${d}`} />
                </div>
              ) : <span className="text-sm text-slate-400">Unavailable</span>}
            </div>
          ))}
        </div>
        <div className="mt-4 space-y-1.5">
          <Label>Notes (optional)</Label>
          <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Anything we should know about your availability" data-testid="availability-notes" />
        </div>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="save-availability-btn" className="btn-primary-gradient gap-2 border-0 text-white">
          <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save Availability"}
        </Button>
      </div>
    </div>
  );
}
